import React, { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";

// For someone who has forgotten their end-to-end encryption passphrase. The encrypted online copy can never be read again, so
// the only way forward is to replace it with the data on this device. This screen makes that consequence unmistakable.
export default function ForgotPassphraseModal({ profileCount, entryCount, onReplace, onCancel }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const hasData = profileCount > 0;
  const ready = typed.trim().toUpperCase() === "REPLACE";

  async function go() {
    if (!ready || busy) return;
    setBusy(true); setErr("");
    try { await onReplace(); }
    catch (e) { setErr(e.message || "Couldn't replace the online copy."); setBusy(false); }
  }

  return (
    <div className="overlay-backdrop fixed inset-0 z-[60] flex items-end lg:items-center justify-center" role="dialog" aria-modal="true" aria-label="Forgot encryption passphrase">
      <div className="bg-card2 w-full max-w-md rounded-t-3xl lg:rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
        <KeyRound size={20} className="text-brass mb-2" />
        <h3 className="v-display text-lg font-semibold">Forgot your encryption passphrase?</h3>
        <p className="text-xs text-ivory-dim mt-1 leading-relaxed">It can't be recovered or reset by anyone, including us, because it never leaves your devices. The encrypted online copy can no longer be read.</p>

        <div className="rounded-xl p-3 mt-4 text-xs leading-relaxed" style={{ background: "var(--brass-dim)" }}>
          <div className="font-semibold mb-1">Try this first</div>
          If another phone or computer is still unlocked (it opens your data without asking for the passphrase), use it: Settings, Account &amp; sync, Change passphrase. Nothing is lost that way.
        </div>

        {hasData ? (
          <>
            <div className="text-xs mt-4 mb-1 font-semibold">Otherwise, replace the online copy with this device</div>
            <p className="text-xs text-ivory-dim leading-relaxed">This device has <span className="text-ivory">{profileCount} profile{profileCount === 1 ? "" : "s"}</span> and <span className="text-ivory">{entryCount} entr{entryCount === 1 ? "y" : "ies"}</span>. They will become your account's data again, unencrypted, and replace what is stored online.</p>
            <div className="text-xs mt-3 mb-1 font-semibold" style={{ color: "var(--rust)" }}>What you lose</div>
            <ul className="text-xs text-ivory-dim list-disc pl-5 space-y-1 leading-relaxed">
              <li>Anything that exists only in the old encrypted online copy, for example entries added on another device that never reached this one. That cannot be recovered.</li>
            </ul>
            <div className="text-xs mt-3 mb-1 font-semibold">What happens next</div>
            <ul className="text-xs text-ivory-dim list-disc pl-5 space-y-1 leading-relaxed">
              <li>Your other devices will pick up the new copy. Encryption is turned off, and you can turn it on again with a new passphrase. Save it in a password manager this time.</li>
            </ul>
            <div className="text-xs text-ivory-dim mt-4 mb-1">Type REPLACE to confirm</div>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoCapitalize="characters" autoComplete="off" className="w-full rounded-lg px-3 py-2.5 text-sm" />
          </>
        ) : (
          <div className="text-xs text-ivory-dim mt-4 leading-relaxed">
            This device has no data to put in its place. If you have a backup file, restore it first (Settings, Back up / Restore) and come back here. Otherwise your only other option is to delete the account (Settings, Account &amp; sync) and start again.
          </div>
        )}

        {err && <div className="text-xs mt-3" style={{ color: "var(--rust)" }}>{err}</div>}
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 bg-card rounded-full py-2.5 text-sm font-medium">{hasData ? "Keep waiting" : "Close"}</button>
          {hasData && <button onClick={go} disabled={!ready || busy} className="flex-1 flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold disabled:opacity-40" style={{ background: "var(--rust)", color: "#fff" }}>{busy ? <Loader2 size={14} className="animate-spin" /> : null} Replace online copy</button>}
        </div>
      </div>
    </div>
  );
}
